import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ArrowDown, ArrowUp, Filter, Plus, Save, X, Zap } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import { FormField } from '@/components/FormField';
import { Button } from '@/components/Button';
import { SectionHeader } from '@/components/Card';
import { Template } from '@/services/templates.service';
import {
  workflowsService, WorkflowAction, WorkflowActionKind, WorkflowConditionTree, WorkflowRule, WorkflowTrigger, CreateRuleInput,
} from '@/services/workflows.service';
import { ConditionEditor, WorkflowLookups } from './ConditionEditor';
import {
  ACTION_LABELS, EDITABLE_ACTIONS, FIELDS_BY_TRIGGER, LEAD_STATUS_OPTIONS, TRIGGER_LABELS, VARIABLE_SOURCES, composeVariable, parseVariable,
} from './workflowMeta';

export interface EditorLookups extends WorkflowLookups {
  templates: Template[];
  accountLabels: Record<string, string>;
}

const DEFAULT_CONFIG: Record<WorkflowActionKind, Record<string, unknown>> = {
  send_template: { templateUid: '', variables: {} },
  create_task: { title: 'Follow up', dueInHours: 24, assignee: 'record_owner', priority: 'normal' },
  assign_owner: { ownerUserUid: 'round_robin' },
  update_field: { field: 'status', value: 'contacted' },
  delay: { minutes: 1440 },
  condition: { when: {} },
  send_webhook: { url: '' },
  branch: {},
};

const UNITS = [{ value: 1, label: 'minutes' }, { value: 60, label: 'hours' }, { value: 1440, label: 'days' }];

function templateVariables(template?: Template): string[] {
  if (!template) return [];
  const names = [...Object.values(template.headerVariableMap ?? {}), ...Object.values(template.variableMap ?? {})];
  return names.filter((name, index) => !!name && names.indexOf(name) === index);
}

interface Props {
  existing: WorkflowRule | null;
  lookups: EditorLookups;
  onClose: () => void;
  onSaved: () => void;
}

export function WorkflowRuleEditor({ existing, lookups, onClose, onSaved }: Props) {
  const [name, setName] = useState(existing?.name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [trigger, setTrigger] = useState<WorkflowTrigger>(existing?.trigger ?? 'lead.created');
  const [conditions, setConditions] = useState<WorkflowConditionTree>(existing?.conditions ?? {});
  const [actions, setActions] = useState<WorkflowAction[]>(
    existing?.actions?.length ? existing.actions : [{ kind: 'create_task', config: { ...DEFAULT_CONFIG.create_task } }],
  );
  const [busy, setBusy] = useState(false);
  const fields = FIELDS_BY_TRIGGER[trigger];
  const templatesByUid = useMemo(() => new Map(lookups.templates.map(t => [t.templateUid, t])), [lookups.templates]);

  const setConfig = (index: number, patch: Record<string, unknown>) =>
    setActions(prev => prev.map((a, i) => (i === index ? { ...a, config: { ...a.config, ...patch } } : a)));
  const move = (index: number, delta: number) => setActions(prev => {
    const next = [...prev]; const target = index + delta;
    if (target < 0 || target >= next.length) return prev;
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  });

  const save = async () => {
    if (!name.trim()) { toast.error('Give the rule a name'); return; }
    if (!actions.length) { toast.error('Add at least one step'); return; }
    setBusy(true);
    try {
      const dto: CreateRuleInput = { name: name.trim(), description, trigger, conditions, actions };
      if (existing) await workflowsService.update(existing.ruleUid, dto);
      else await workflowsService.create(dto);
      toast.success(existing ? 'Rule updated' : 'Rule created');
      onSaved();
    } catch { /* the API client already explained the refusal */ } finally { setBusy(false); }
  };

  const teamOptions = lookups.team.map(u => <option key={u.userUid} value={u.userUid}>{u.label}</option>);

  const stepBody = (a: WorkflowAction, i: number) => {
    const c = a.config;
    switch (a.kind) {
      case 'send_template': {
        const template = templatesByUid.get(String(c.templateUid ?? ''));
        const variables = (c.variables ?? {}) as Record<string, string>;
        return (
          <>
            <FormField label="Template" hint="Only approved templates can be sent. Marketing templates skip numbers that opted out.">
              <Select value={String(c.templateUid ?? '')} onChange={e => setConfig(i, { templateUid: e.target.value, variables: {} })}>
                <option value="">Choose an approved template…</option>
                {lookups.templates.map(t => (
                  <option key={t.templateUid} value={t.templateUid}>
                    {t.name} · {t.language} · {t.category}{t.accountUid && lookups.accountLabels[t.accountUid] ? ` · ${lookups.accountLabels[t.accountUid]}` : ''}
                  </option>
                ))}
              </Select>
            </FormField>
            {String(c.templateUid ?? '') && !template && (
              <p className="text-2xs text-warning">This template is no longer approved or was removed; the step will fail until you choose another.</p>
            )}
            {templateVariables(template).map(variable => {
              const parsed = parseVariable(variables[variable]);
              const set = (source: string, text: string) => setConfig(i, { variables: { ...variables, [variable]: composeVariable(source, text) } });
              return (
                <div key={variable} className="grid grid-cols-1 gap-2 sm:grid-cols-[8rem_1fr_1fr] sm:items-center">
                  <code className="truncate text-2xs text-text-muted">{`{{${variable}}}`}</code>
                  <Select value={parsed.source} onChange={e => set(e.target.value, parsed.source === 'text' ? '' : parsed.text)} className="!h-9 !text-xs">
                    {VARIABLE_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </Select>
                  <Input value={parsed.text} onChange={e => set(parsed.source, e.target.value)} className="!h-9 !text-xs"
                    placeholder={parsed.source === 'text' ? 'Text to send' : 'Fallback if empty (e.g. there)'} />
                </div>
              );
            })}
          </>
        );
      }
      case 'create_task':
        return (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <FormField label="Task title"><Input value={String(c.title ?? '')} onChange={e => setConfig(i, { title: e.target.value })} placeholder="Call the new lead" /></FormField>
            <FormField label="Due in (hours)"><Input type="number" min={0} value={String(c.dueInHours ?? 24)} onChange={e => setConfig(i, { dueInHours: Number(e.target.value) })} /></FormField>
            <FormField label="Assign to">
              <Select value={String(c.assignee ?? c.assigneeUserUid ?? 'record_owner')} onChange={e => setConfig(i, { assignee: e.target.value, assigneeUserUid: undefined })}>
                <option value="record_owner">The lead / deal owner (else the rule's creator)</option>
                {teamOptions}
              </Select>
            </FormField>
            <FormField label="Priority">
              <Select value={String(c.priority ?? 'normal')} onChange={e => setConfig(i, { priority: e.target.value })}>
                {['low', 'normal', 'high', 'urgent'].map(p => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
              </Select>
            </FormField>
          </div>
        );
      case 'assign_owner':
        return (
          <FormField label="Owner" hint="Round robin picks the active teammate with the fewest open leads.">
            <Select value={String(c.ownerUserUid ?? 'round_robin')} onChange={e => setConfig(i, { ownerUserUid: e.target.value })}>
              <option value="round_robin">Round robin</option>
              {teamOptions}
            </Select>
          </FormField>
        );
      case 'update_field': {
        const field = c.field === 'notes' ? 'note' : String(c.field ?? 'status');
        return (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <FormField label="Change">
              <Select value={field} onChange={e => setConfig(i, { field: e.target.value, value: e.target.value === 'status' ? 'contacted' : '' })}>
                <option value="status">Lead status</option>
                <option value="estimatedValueAmount">Estimated value</option>
                <option value="nextFollowUpInHours">Follow-up reminder</option>
                <option value="note">Add a note</option>
              </Select>
            </FormField>
            <FormField label={field === 'nextFollowUpInHours' ? 'Remind in (hours)' : field === 'note' ? 'Note' : 'New value'}>
              {field === 'status' ? (
                <Select value={String(c.value ?? '')} onChange={e => setConfig(i, { value: e.target.value })}>
                  {LEAD_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
              ) : field === 'note' ? (
                <Textarea rows={2} value={String(c.value ?? '')} onChange={e => setConfig(i, { value: e.target.value })} />
              ) : (
                <Input type="number" min={field === 'nextFollowUpInHours' ? 1 : 0} value={String(c.value ?? '')} onChange={e => setConfig(i, { value: e.target.value === '' ? '' : Number(e.target.value) })} />
              )}
            </FormField>
          </div>
        );
      }
      case 'delay': {
        const minutes = Number(c.minutes ?? 60);
        const unit = minutes % 1440 === 0 ? 1440 : minutes % 60 === 0 ? 60 : 1;
        return (
          <div className="grid grid-cols-[1fr_1fr] gap-2">
            <FormField label="Wait for"><Input type="number" min={1} value={String(minutes / unit)} onChange={e => setConfig(i, { minutes: Math.round(Number(e.target.value || 0) * unit) })} /></FormField>
            <FormField label="Unit">
              <Select value={String(unit)} onChange={e => setConfig(i, { minutes: Math.round((minutes / unit) * Number(e.target.value)) })}>
                {UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
              </Select>
            </FormField>
            <p className="col-span-2 text-2xs text-text-muted">The steps below run after the wait (checked about once a minute, up to 90 days). A waiting run that comes due while the rule is off is cancelled.</p>
          </div>
        );
      }
      case 'condition':
        return (
          <ConditionEditor value={(c.when ?? {}) as WorkflowConditionTree} onChange={when => setConfig(i, { when })} fields={fields} lookups={lookups}
            emptyText="Add at least one condition. “(now)” fields read the record at this moment — ideal right after a wait." />
        );
      case 'send_webhook':
        return (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <FormField label="HTTPS URL" hint="Public https:// endpoints only."><Input value={String(c.url ?? '')} onChange={e => setConfig(i, { url: e.target.value })} placeholder="https://hooks.example.com/…" /></FormField>
            <FormField label="Secret (optional)" hint="Sent as the X-Workflow-Secret header.">
              <Input type="password" value={String(c.secret ?? '')} onChange={e => setConfig(i, { secret: e.target.value })} placeholder={c.hasSecret ? 'Saved — leave blank to keep' : ''} />
            </FormField>
          </div>
        );
      case 'branch':
        return <p className="text-2xs text-text-muted">This if/else step was created through the API. It keeps working as saved; edit it through the API.</p>;
      default:
        return null;
    }
  };

  return (
    <Modal open onClose={onClose} title={existing ? 'Edit workflow rule' : 'New workflow rule'} widthClass="max-w-3xl">
      <div className="space-y-4 px-6 py-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField label="Rule name" required>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Welcome new Google leads" />
          </FormField>
          <FormField label="When" required>
            <Select value={trigger} onChange={e => { setTrigger(e.target.value as WorkflowTrigger); setConditions({}); }}>
              {(Object.keys(TRIGGER_LABELS) as WorkflowTrigger[]).map(t => <option key={t} value={t}>{TRIGGER_LABELS[t]}</option>)}
            </Select>
          </FormField>
        </div>
        <FormField label="Description">
          <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} placeholder="What this rule does and why" />
        </FormField>

        <div className="rounded-xl border border-border bg-surface-alt/40 p-3">
          <SectionHeader title="Only if" subtitle="Optional — leave empty to run on every event." icon={<Filter size={14} />} />
          <ConditionEditor value={conditions} onChange={setConditions} fields={fields} lookups={lookups} emptyText="Runs for every event of this type." />
        </div>

        <SectionHeader title="Steps" subtitle="Run in order. A failed step is logged and the next step still runs." icon={<Zap size={14} />} />
        <div className="space-y-3">
          {actions.map((a, i) => (
            <div key={i} className="rounded-xl border border-border bg-surface-alt/40 p-3">
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-soft text-2xs font-semibold text-primary">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <Select value={a.kind} disabled={a.kind === 'branch'}
                    onChange={e => { const kind = e.target.value as WorkflowActionKind; setActions(prev => prev.map((x, idx) => (idx === i ? { kind, config: { ...DEFAULT_CONFIG[kind] } } : x))); }}>
                    {(a.kind === 'branch' ? ['branch' as WorkflowActionKind] : EDITABLE_ACTIONS).map(k => <option key={k} value={k}>{ACTION_LABELS[k]}</option>)}
                  </Select>
                </div>
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="rounded-lg p-2 text-text-muted hover:bg-surface-alt disabled:opacity-30" aria-label="Move step up"><ArrowUp size={13} /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === actions.length - 1} className="rounded-lg p-2 text-text-muted hover:bg-surface-alt disabled:opacity-30" aria-label="Move step down"><ArrowDown size={13} /></button>
                <button type="button" onClick={() => setActions(prev => prev.filter((_, idx) => idx !== i))}
                  className="rounded-lg p-2 text-text-muted hover:bg-danger-soft hover:text-danger" aria-label="Remove step"><X size={14} /></button>
              </div>
              <div className="mt-3 space-y-2 text-xs">{stepBody(a, i)}</div>
            </div>
          ))}
          <Button size="sm" variant="ghost" iconLeft={<Plus size={13} />} onClick={() => setActions(prev => [...prev, { kind: 'create_task', config: { ...DEFAULT_CONFIG.create_task } }])}>
            Add a step
          </Button>
        </div>

        <div className="-mx-6 -mb-5 mt-4 flex justify-end gap-2 border-t border-border px-6 pb-4 pt-4">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} loading={busy} iconLeft={<Save size={14} />}>{existing ? 'Save changes' : 'Create rule'}</Button>
        </div>
      </div>
    </Modal>
  );
}
